'use client';

import { useTranslation } from '@/app/i18n/client';
import AppContext from '@/components/AppContext';
import SectionDivider from '@/components/SectionDivider';
import useCryptoSupport from '@/hooks/useCryptoSupport';
import encryptPayload from '@/lib/encryptPayload';
import { yupResolver } from '@hookform/resolvers/yup';
import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTime } from 'luxon';
import { useContext } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { TransitionGroup } from 'react-transition-group';
import { InferType, array, boolean, object, string } from 'yup';
import PeopleEntry, { peopleSchema } from './PeopleEntry';
import PrivacyNote from './PrivacyNote';

export type FieldSpec<T> = {
  name: T;
  label: string;
  helperText?: string;
  values?: string[];
};

const MAX_PEOPLE = 20;

const formSchema = object({
  organization: string().required(`Nazwa firmy jest wymagana`),
  anonymous: string().required(`Musisz określić czy zgłaszasz się anonimowo`),
  relation: string().required(
    `Musisz określić kim jesteś dla wybranej przez Ciebie firmy`
  ),
  firstName: string().when('anonymous', {
    is: (v: any) => v === true || v == 'Nie',
    then: (schema) => schema.required('Imię jest wymagane'),
  }),
  lastName: string().optional(),
  email: string().email(),
  phone: string().when(['anonymous', 'email'], {
    is: (anonymous: string, email: string) => anonymous == 'Nie' && email == '',
    then: (schema) =>
      schema.required('Wymagane jest podanie numeru telefonu lub adresu email'),
  }),
  people: array().of(peopleSchema).optional(),
  managementAware: string().optional(),
  description: string(),
  place: string(),
  incidentDate: string().required(`Data jest wymagana`),
  incidentFrom: string().required(`To pole jest wymagane`),
  incidentSource: string(),
  peopleWhoKnew: string(),
  processing: boolean().oneOf(
    [true],
    `Musisz wyrazić zgodę na przetwarzanie danych abyśmy mogli zapisać to zgłoszenie`
  ),
  share: boolean().oneOf(
    [true],
    `Musisz wyrazić tą zgodę abyśmy mogli przekazać Twoje zgłoszenie do adresata`
  ),
})
  .noUnknown()
  .required();

export type RootInputs = InferType<typeof formSchema>;
type RootFieldNames = keyof Required<Omit<RootInputs, 'people'>>;
type RootFields = {
  [key in RootFieldNames]: FieldSpec<RootFieldNames>;
};

export interface ReportProps {
  settings: any;
  setError: (error: string) => void;
}

export default function Report({ setError, settings }: ReportProps) {
  const app = useContext(AppContext);
  const { t } = useTranslation(app.i18n.lng);
  const cryptoSupport = useCryptoSupport();

  const testValues: RootInputs = {
    people: [{ firstName: 'Jan', lastName: 'Kowalski', position: 'Kierownik' }],
    organization: 'VENTOR ENERGI NORGE',
    relation: 'Pracownikiem',
    anonymous: 'Nie',
    firstName: 'Jan',
    lastName: 'Kowalski',
    email: 'jan.kowalski@example.com',
    incidentFrom: 'Zdarzyło się tylko raz',
    incidentDate: DateTime.now().toISODate(),
    processing: true,
    share: true,
  };
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting, isSubmitSuccessful, isValid },
  } = useForm<RootInputs>({
    resolver: yupResolver(formSchema) as any,
    mode: 'onChange',
    defaultValues: {
      people: [
        {
          firstName: '',
          lastName: '',
          position: '',
        },
      ],
      processing: false,
      share: false,
      ...testValues,
    },
  });
  const {
    fields: peopleFields,
    append,
    prepend,
    remove,
    swap,
    move,
    insert,
    update,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'people', // unique name for your Field Array
  });
  const anonymous = watch('anonymous');

  const onSubmit = async (data: RootInputs) => {
    setError('');
    try {
      const request = cryptoSupport.supported
        ? {
            tenant: 'ventor',
            kind: 'encrypted',
            payload: await encryptPayload(
              window.crypto,
              settings.recipients,
              JSON.stringify(data)
            ),
          }
        : {
            tenant: 'ventor',
            kind: 'plain',
            payload: JSON.stringify(data),
          };

      const response = await fetch('/api/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      if (response.ok) {
      } else {
        setError('Podczas wysyłania raportu wystąpił błąd.');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleClose = () => {
    setError('');
  };

  const spec: RootFields = {
    organization: {
      name: 'organization',
      label: 'Wybierz firmę, której chciałbyś zgłosić incydent.',
      values: [
        // 'VENTOR ENERGI',
        'VENTOR ENERGI NORGE',
        'VENTIGROS',
        // 'VENTOR CONSTRUCTION',
        'ISONOR POLSKA',
      ],
    },
    anonymous: {
      name: 'anonymous',
      label: 'Czy chciałbyś wysłać ten formularz anonimowo?',
      values: ['Tak', 'Nie'],
    },
    relation: {
      name: 'relation',
      label: 'Kim jesteś dla wybranej przez Ciebie firmy?',
      values: [
        'Pracownikiem',
        'Dostawcą',
        'Podwykonawcą',
        'Zewnętrzną jednostką',
      ],
    },
    firstName: {
      name: 'firstName',
      label: 'Imię',
    },
    lastName: {
      name: 'lastName',
      label: 'Nazwisko',
    },
    email: {
      name: 'email',
      label: 'Adres e-mail',
    },
    phone: {
      name: 'phone',
      label: 'Numer telefonu kontaktowego',
    },
    managementAware: {
      name: 'managementAware',
      label: 'Czy kierownictwo ma świadomość tego problemu?',
      values: ['Tak', 'Nie', 'Nie wiem'],
    },
    description: {
      name: 'description',
      label: 'Opis incydentu',
      helperText: 'Przedstaw krótki opis incydentu.',
    },
    place: {
      name: 'place',
      label: 'Gdzie doszło do incydentu?',
      helperText:
        'Jeśli jesteś w stanie wskazać miejsce wystąpienia incydentu to wpisz je tutaj, jeśli nie pozostaw to pole puste.',
    },
    incidentDate: {
      name: 'incidentDate',
      label: 'Data wystąpienia incydentu',
      helperText: 'Podaj dokładną lub przybliżoną datę wystąpienia incydentu.',
    },
    incidentFrom: {
      name: 'incidentFrom',
      label: 'Od jak dawna incydent ma miejsce',
      values: [
        'Zdarzyło się tylko raz',
        'W ostatnim tygodniu',
        'W ostatnim miesiącu',
        'W ostatnim kwartale',
        'W ostatnim roku',
        'Więcej niż rok temu',
        'Nie wiem',
      ],
    },
    incidentSource: {
      name: 'incidentSource',
      label: 'W jaki sposób dowiedziałeś się o incydencie?',
      values: [
        'Zdarzyło się to mi osobiście',
        'Byłem świadkiem incydentu',
        'Słyszałem o tym incydencie',
        'Informację przekazał mi kolega z pracy',
        'Informację przekazała mi osoba nie związana z firmą',
        'Krążą plotki na ten temat',
        'Wszedłem w posiadanie dokumentu/pliku',
        'Inny',
      ],
    },
    peopleWhoKnew: {
      name: 'peopleWhoKnew',
      label: 'Wskaż osoby, które wiedziały o problemie, ale nie zareagowały',
      helperText:
        'Podaj osoby i opisz ich zachowania, np. Jan Kowalski (kierownik) – powiedział mi żeby się tym nie przejmował.',
    },
    processing: {
      name: 'processing',
      label:
        'wyrażam zgodę na przetwarzanie podanych danych przez okres jednego roku lat w celu obsługi przesłanego zgłoszenia',
    },
    share: {
      name: 'share',
      label:
        'wyrażam zgodę na udostępnianie moich danych firmie której zgłoszenie dotyczy',
    },
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={app.i18n.lng}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={spec.organization.name}
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ my: 1 }} fullWidth required error={!!error}>
              <InputLabel id={`${spec.organization.name}-label`}>
                {spec.organization.label}
              </InputLabel>
              <Select
                labelId={`${spec.organization.name}-label`}
                id={`${spec.organization.name}`}
                label={spec.organization.label}
                {...field}
              >
                {spec.organization.values?.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              {!!error && <FormHelperText>{error?.message}</FormHelperText>}
            </FormControl>
          )}
        />

        <Grid container spacing={2} sx={{ my: 1 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={spec.anonymous.name}
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth required error={!!error}>
                  <InputLabel id={`${spec.anonymous.name}-label`}>
                    {spec.anonymous.label}
                  </InputLabel>
                  <Select
                    labelId={`${spec.anonymous.name}-label`}
                    id={`${spec.anonymous.name}`}
                    label={spec.anonymous.label}
                    {...field}
                  >
                    {spec.anonymous.values?.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!error && <FormHelperText>{error?.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={spec.relation.name}
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth required error={!!error}>
                  <InputLabel id={`${spec.relation.name}-label`}>
                    {spec.relation.label}
                  </InputLabel>
                  <Select
                    labelId={`${spec.relation.name}-label`}
                    id={`${spec.relation.name}`}
                    label={spec.relation.label}
                    {...field}
                  >
                    {spec.relation.values?.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!error && <FormHelperText>{error?.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <TransitionGroup>
          {anonymous === 'Nie' && (
            <Collapse>
              <Box>
                <SectionDivider sx={{ my: 2 }}>
                  <Typography variant="subtitle1">
                    Podaj swoje dane kontaktowe, abyśmy mogli się z Tobą
                    skontaktować.
                  </Typography>
                </SectionDivider>

                <Grid container spacing={2} sx={{ my: 1 }}>
                  <Grid size={6}>
                    <Controller
                      name={spec.firstName.name}
                      control={control}
                      defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth required error={!!error}>
                          <TextField
                            id={`${spec.firstName.name}`}
                            label={spec.firstName.label}
                            {...field}
                            required
                            error={!!error}
                            helperText={error?.message}
                          />
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid size={6}>
                    <Controller
                      name={spec.lastName.name}
                      control={control}
                      defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth error={!!error}>
                          <TextField
                            id={`${spec.lastName.name}`}
                            label={spec.lastName.label}
                            {...field}
                            error={!!error}
                            helperText={error?.message}
                          />
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ my: 1 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name={spec.email.name}
                      control={control}
                      defaultValue=""
                      render={({
                        field,
                        fieldState: { error },
                        formState: {},
                      }) => (
                        <FormControl fullWidth error={!!error}>
                          <TextField
                            id={`${spec.email.name}`}
                            label={spec.email.label}
                            {...field}
                            error={!!error}
                            helperText={error?.message}
                            required
                          />
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name={spec.phone.name}
                      control={control}
                      defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth error={!!error}>
                          <TextField
                            id={`${spec.phone.name}`}
                            label={spec.phone.label}
                            {...field}
                            error={!!error}
                            helperText={error?.message}
                            required
                          />
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          )}
        </TransitionGroup>

        <SectionDivider sx={{ my: 2 }}>
          <Typography>Wymień osoby biorące udział w incydencie</Typography>
        </SectionDivider>

        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <TransitionGroup>
            {peopleFields.map((field, index, arr) => (
              <Collapse key={field.id}>
                <PeopleEntry
                  control={control}
                  append={append}
                  update={update}
                  remove={remove}
                  lastItem={peopleFields.length === index + 1}
                  index={index}
                  allowNew={arr.length < MAX_PEOPLE}
                  maxEntries={MAX_PEOPLE}
                  value={field}
                />
              </Collapse>
            ))}
          </TransitionGroup>
        </List>

        <SectionDivider sx={{ my: 2 }}>
          <Typography>Szczegóły incydentu</Typography>
        </SectionDivider>

        <Controller
          name={spec.managementAware.name}
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ my: 1 }} fullWidth error={!!error}>
              <InputLabel id={`${spec.managementAware.name}-label`}>
                {spec.managementAware.label}
              </InputLabel>
              <Select
                labelId={`${spec.managementAware.name}-label`}
                id={`${spec.managementAware.name}`}
                label={spec.managementAware.label}
                {...field}
              >
                {spec.managementAware.values?.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Controller
          name={spec.description.name}
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ my: 1 }} fullWidth error={!!error}>
              <TextField
                id={`${spec.description.name}`}
                label={spec.description.label}
                multiline
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message ?? spec.description.helperText}
              />
            </FormControl>
          )}
        />

        <Controller
          name={spec.place.name}
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ my: 1 }} fullWidth error={!!error}>
              <TextField
                id={`${spec.place.name}`}
                label={spec.place.label}
                multiline
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message ?? spec.place.helperText}
              />
            </FormControl>
          )}
        />

        <Grid container spacing={2} sx={{ my: 1 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={spec.incidentDate.name}
              control={control}
              defaultValue=""
              render={({
                field: { value, onChange, ...field },
                fieldState: { error },
              }) => (
                <FormControl fullWidth required error={!!error}>
                  <DatePicker
                    label={spec.incidentDate.label}
                    disableFuture
                    {...field}
                    value={DateTime.fromISO(value as string)}
                    onChange={(newValue) => {
                      if (newValue) {
                        onChange(newValue.toISODate());
                      }
                    }}
                    slotProps={{
                      field: {
                        clearable: true,
                        onClear: () => onChange(''),
                      },
                      textField: {
                        required: true,
                        error: !!error,
                        helperText:
                          error?.message ?? spec.incidentDate.helperText,
                      },
                    }}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={spec.incidentFrom.name}
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth required error={!!error}>
                  <InputLabel id={`${spec.incidentFrom.name}-label`}>
                    {spec.incidentFrom.label}
                  </InputLabel>
                  <Select
                    labelId={`${spec.incidentFrom.name}-label`}
                    id={`${spec.incidentFrom.name}`}
                    label={spec.incidentFrom.label}
                    {...field}
                  >
                    {spec.incidentFrom.values?.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!error && <FormHelperText>{error?.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <Controller
          name={spec.incidentSource.name}
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ my: 1 }} fullWidth error={!!error}>
              <InputLabel id={`${spec.incidentSource.name}-label`}>
                {spec.incidentSource.label}
              </InputLabel>
              <Select
                labelId={`${spec.incidentSource.name}-label`}
                id={`${spec.incidentSource.name}`}
                label={spec.incidentSource.label}
                {...field}
              >
                {spec.incidentSource.values?.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              {!!error && <FormHelperText>{error?.message}</FormHelperText>}
            </FormControl>
          )}
        />

        <Controller
          name={spec.peopleWhoKnew.name}
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ my: 1 }} fullWidth error={!!error}>
              <TextField
                id={`${spec.peopleWhoKnew.name}`}
                label={spec.peopleWhoKnew.label}
                multiline
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message ?? spec.peopleWhoKnew.helperText}
              />
            </FormControl>
          )}
        />

        <Typography variant="subtitle1" sx={{ my: 2 }}>
          Podaj wszystkie szczegóły dotyczące domniemanego naruszenia, w tym
          lokalizację świadków i wszelkie inne informacje, które mogą być cenne
          w ocenie i ostatecznym rozwiązaniu tej sytuacji.
        </Typography>
        <Typography variant="subtitle2" sx={{ my: 2 }}>
          Prosimy o poświęcenie swojego czasu i podanie jak największej liczby
          szczegółów, proszę nie podawać szczegółów, które mogą ujawnić Twoją
          tożsamość, chyba że chcesz to zrobić. W niektórych przypadkach może
          być to istotne, abyśmy mieli świadomość, że jesteś jedyną osobą
          świadomą tej sytuacji.
        </Typography>

        <PrivacyNote />

        <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>
          Po uważnym przeczytaniu powyższego oświadczenia, dobrowolnie:
        </Typography>

        <Controller
          name={spec.processing.name}
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ my: 1 }} fullWidth error={!!error}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={field.onChange}
                    name={field.name}
                    required
                  />
                }
                label={spec.processing.label}
              />
              {!!error && <FormHelperText>{error?.message}</FormHelperText>}
            </FormControl>
          )}
        />

        <Controller
          name={spec.share.name}
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ my: 1 }} fullWidth required error={!!error}>
              <FormControlLabel
                control={
                  <Checkbox checked={!!field.value} {...field} required />
                }
                label={spec.share.label}
              />
              {!!error && <FormHelperText>{error?.message}</FormHelperText>}
            </FormControl>
          )}
        />

        <Box
          sx={{
            my: 4,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'end',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {!isDirty ? (
              <Alert severity="warning">Formularz nie jest uzupełniony</Alert>
            ) : !isValid ? (
              <Alert severity="error">Formularz zawiera błędy</Alert>
            ) : null}
            <Button
              role="submit"
              variant="contained"
              disabled={!isDirty}
              onClick={handleSubmit(onSubmit)}
              endIcon={<SendIcon />}
              loading={isSubmitting}
              loadingPosition="end"
              size="large"
            >
              Wyślij
            </Button>
          </Box>
        </Box>
      </form>
    </LocalizationProvider>
  );
}
